import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import type { Approval, Incident, Task } from '@shared/types';

/**
 * Notification bell — a single quiet inbox for the three things that need a
 * human: pending approvals, open incidents, and tasks awaiting the founder.
 * Polls every 20s; each source fails soft (an unreachable endpoint counts 0).
 */

const POLL_MS = 20_000;

interface Bucket {
  count: number;
  titles: string[]; // up to 2, for the dropdown preview
}

const EMPTY: Bucket = { count: 0, titles: [] };

function bucket<T>(rows: T[], title: (r: T) => string): Bucket {
  return { count: rows.length, titles: rows.slice(0, 2).map(title) };
}

/**
 * One blocker per (agent, kind) — mirrors the Dashboard "Needs your eyes" rule.
 * A nightly job that parks `awaiting_founder` on the same unresolved blocker
 * (e.g. Leo's analytics-ingest waiting on the CheckoutChamp IP whitelist) files a
 * fresh task every run; without this the bell counts one thing N times and its
 * badge creeps up daily, training founders to ignore it. Keep the newest task of
 * each group so the count matches what the Dashboard shows (1, not the pile).
 */
function dedupeByAgentKind(rows: Task[]): Task[] {
  const newest = new Map<string, Task>();
  for (const t of rows) {
    const key = `${t.agent_id}|${t.kind}`;
    const prev = newest.get(key);
    const when = t.finished_at ?? t.created_at;
    const prevWhen = prev ? (prev.finished_at ?? prev.created_at) : '';
    if (!prev || when > prevWhen) newest.set(key, t);
  }
  return [...newest.values()];
}

function BellIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 8-3 8h18s-3-1-3-8" />
      <path d="M13.7 21a2 2 0 0 1-3.4 0" />
    </svg>
  );
}

function Group({ label, b, to, tone, onNav }: { label: string; b: Bucket; to: string; tone: 'amber' | 'coral' | 'ice'; onNav: () => void }) {
  if (b.count === 0) return null;
  return (
    <Link to={to} onClick={onNav} className="nbell-row">
      <span className={`chip chip-${tone}`}>
        <i />
        {b.count}
      </span>
      <span className="nbell-label">
        {label}
        {b.titles.length > 0 && <span className="nbell-titles">{b.titles.join(' · ')}</span>}
      </span>
      <span className="nbell-arrow" aria-hidden>
        →
      </span>
    </Link>
  );
}

export function NotificationBell() {
  const [approvals, setApprovals] = useState<Bucket>(EMPTY);
  const [incidents, setIncidents] = useState<Bucket>(EMPTY);
  const [awaiting, setAwaiting] = useState<Bucket>(EMPTY);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let alive = true;
    const load = () => {
      api
        .get<Approval[]>('/approvals?status=pending')
        .then((rows) => alive && setApprovals(bucket(rows, (a) => a.title)))
        .catch(() => alive && setApprovals(EMPTY));
      api
        .get<Incident[]>('/reports/incidents')
        .then((rows) => alive && setIncidents(bucket(rows.filter((i) => i.status === 'open'), (i) => i.title)))
        .catch(() => alive && setIncidents(EMPTY));
      api
        .get<Task[]>('/tasks?status=awaiting_founder')
        .then((rows) => alive && setAwaiting(bucket(dedupeByAgentKind(rows), (t) => t.title)))
        .catch(() => alive && setAwaiting(EMPTY));
    };
    load();
    const t = setInterval(load, POLL_MS);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  // click-outside closes the dropdown
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const total = approvals.count + incidents.count + awaiting.count;
  const close = () => setOpen(false);

  return (
    <div ref={rootRef} className="nbell">
      <style>{CSS}</style>
      <button
        className="nbell-btn"
        aria-label={total > 0 ? `Notifications — ${total} need you` : 'Notifications — all clear'}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <BellIcon />
        {total > 0 && <span className="nbell-badge mono">{total > 99 ? '99+' : total}</span>}
      </button>

      {open && (
        <div className="nbell-drop" role="menu">
          {total === 0 ? (
            <p className="nbell-zero">All clear — nothing needs you right now.</p>
          ) : (
            <>
              <Group label="Approvals pending" b={approvals} to="/approvals" tone="amber" onNav={close} />
              <Group label="Incidents open" b={incidents} to="/reports" tone="coral" onNav={close} />
              <Group label="Waiting on you" b={awaiting} to="/" tone="ice" onNav={close} />
            </>
          )}
        </div>
      )}
    </div>
  );
}

const CSS = `
.nbell{position:relative}
.nbell-btn{position:relative;display:inline-flex;align-items:center;justify-content:center;width:36px;height:36px;border-radius:var(--r-ctl);border:1px solid var(--line-2);background:transparent;color:var(--dim);cursor:pointer;transition:color .16s,border-color .16s}
.nbell-btn:hover{color:var(--text);border-color:var(--ice)}
.nbell-badge{position:absolute;top:-6px;right:-6px;min-width:16px;height:16px;padding:0 4px;display:inline-flex;align-items:center;justify-content:center;font-size:9.5px;font-weight:600;border-radius:999px;background:var(--coral);color:#fff;line-height:1}
.nbell-drop{position:absolute;right:0;top:calc(100% + 8px);z-index:60;width:300px;background:var(--panel);border:1px solid var(--line-2);border-radius:var(--r);padding:8px;box-shadow:0 10px 40px rgba(0,0,0,.35)}
.nbell-zero{font-size:12.5px;color:var(--dim);padding:12px 10px;text-align:center}
.nbell-row{display:flex;align-items:center;gap:10px;padding:10px;border-radius:var(--r-ctl);color:var(--text)}
.nbell-row:hover{background:rgba(var(--ice-rgb),.06)}
.nbell-row + .nbell-row{margin-top:2px}
.nbell-label{min-width:0;flex:1;font-size:13px;font-weight:500}
.nbell-titles{display:block;font-family:var(--mono);font-size:10.5px;color:var(--faint);margin-top:2px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.nbell-arrow{color:var(--faint);font-size:12px}
`;
